import { h, ref, watch, onMounted, onBeforeUnmount, nextTick, Fragment, Teleport } from 'vue'

function normalizarVNodes (nodes) {
  return (Array.isArray(nodes) ? nodes : [nodes])
    .flat(Infinity)
    .filter(node => node !== null && node !== undefined && typeof node !== 'boolean')
}

function resolveTarget (selector) {
  if (!selector) return null
  if (typeof selector === 'string') {
    return document.querySelector(selector)
  }
  return selector
}

function getOppositePlacement (placement) {
  const oppositePlacement = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
  }

  return oppositePlacement[placement] || 'top'
}

function calculatePosition (rect, popoverRect, placement, gap) {
  switch (placement) {
    case 'top':
      return {
        top: rect.top - popoverRect.height - gap,
        left: rect.left + (rect.width - popoverRect.width) / 2
      }
    case 'bottom':
      return {
        top: rect.bottom + gap,
        left: rect.left + (rect.width - popoverRect.width) / 2
      }
    case 'left':
      return {
        top: rect.top + (rect.height - popoverRect.height) / 2,
        left: rect.left - popoverRect.width - gap
      }
    case 'right':
    default:
      return {
        top: rect.top + (rect.height - popoverRect.height) / 2,
        left: rect.right + gap
      }
  }
}

function overflowsViewport (position, popoverRect, padding) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return (
    position.left < padding ||
    position.left + popoverRect.width > viewportWidth - padding ||
    position.top < padding ||
    position.top + popoverRect.height > viewportHeight - padding
  )
}

function clampPosition (position, popoverRect, padding) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return {
    top: Math.min(
      Math.max(position.top, padding),
      Math.max(padding, viewportHeight - popoverRect.height - padding)
    ),
    left: Math.min(
      Math.max(position.left, padding),
      Math.max(padding, viewportWidth - popoverRect.width - padding)
    )
  }
}

function setPopoverPlacementClass (popoverEl, placement) {
  popoverEl.classList.remove('top', 'bottom', 'left', 'right')
  popoverEl.classList.add(placement)
}

function positionPopover (targetEl, popoverEl, preferredPlacement) {
  if (!targetEl || !popoverEl) return

  const rect = targetEl.getBoundingClientRect()

  popoverEl.style.top = '0px'
  popoverEl.style.left = '0px'
  const popoverRect = popoverEl.getBoundingClientRect()

  const gap = 10
  const padding = 8

  let finalPlacement = preferredPlacement
  let position = calculatePosition(rect, popoverRect, finalPlacement, gap)

  if (overflowsViewport(position, popoverRect, padding)) {
    const fallbackPlacement = getOppositePlacement(preferredPlacement)
    const fallbackPosition = calculatePosition(rect, popoverRect, fallbackPlacement, gap)

    finalPlacement = fallbackPlacement
    position = fallbackPosition
  }

  const clampedPosition = clampPosition(position, popoverRect, padding)

  popoverEl.dataset.pttPopoverPlacement = finalPlacement
  setPopoverPlacementClass(popoverEl, finalPlacement)
  popoverEl.style.top = clampedPosition.top + 'px'
  popoverEl.style.left = clampedPosition.left + 'px'
}

function attachListener (target, eventName, handler, options) {
  if (target?.addEventListener) {
    target.addEventListener(eventName, handler, options)
  }
}

function detachListener (target, eventName, handler, options) {
  if (target?.removeEventListener) {
    target.removeEventListener(eventName, handler, options)
  }
}

export default {
  name: 'PttPopover',
  props: {
    target: { type: String, default: null },
    trigger: { type: String, default: 'click' },
    placement: { type: String, default: 'top' },
    title: { type: String, default: '' },
    modelValue: { type: Boolean, default: null },
    enterable: { type: Boolean, default: true },
    customClass: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'show', 'hide'],
  setup (props, { emit }) {
    const visible = ref(false)
    const popoverRef = ref(null)
    let targetEl = null
    let hideTimeout = null
    let targetClickHandler = null
    let animationFrameId = null

    const isManual = () => props.trigger === 'manual'
    const isHover = () => props.trigger === 'hover'
    const isFocus = () => props.trigger === 'focus'

    function getModelValue () {
      return props.modelValue
    }

    function emitModel (val) {
      emit('update:modelValue', val)
    }

    function setPopoverRef (el) {
      popoverRef.value = el
    }

    function repositionPopover () {
      if (!visible.value) return

      nextTick(() => {
        const popoverEl = popoverRef.value
        if (popoverEl && targetEl) {
          positionPopover(targetEl, popoverEl, props.placement)
        }
      })
    }

    function stopTrackingPosition () {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    }

    function trackPosition () {
      stopTrackingPosition()

      const loop = () => {
        if (!visible.value) {
          animationFrameId = null
          return
        }

        repositionPopover()
        animationFrameId = requestAnimationFrame(loop)
      }

      animationFrameId = requestAnimationFrame(loop)
    }

    function showPopover () {
      if (hideTimeout) {
        clearTimeout(hideTimeout)
        hideTimeout = null
      }
      visible.value = true
      emitModel(true)
      emit('show')
      repositionPopover()
      trackPosition()
    }

    function hidePopover () {
      if (hideTimeout) {
        clearTimeout(hideTimeout)
        hideTimeout = null
      }
      stopTrackingPosition()
      visible.value = false
      emitModel(false)
      emit('hide')
    }

    function scheduleHide () {
      if (!isHover()) {
        return
      }

      if (hideTimeout) {
        clearTimeout(hideTimeout)
      }
      hideTimeout = setTimeout(() => {
        hidePopover()
      }, isHover() ? 150 : 0)
    }

    function onPopoverMouseenter () {
      if (!isHover() || !props.enterable) {
        return
      }

      if (hideTimeout) {
        clearTimeout(hideTimeout)
        hideTimeout = null
      }
    }

    function onPopoverMouseleave () {
      if (!isHover() || !props.enterable) {
        return
      }

      scheduleHide()
    }

    function onDocumentClick (e) {
      if (!visible.value) return
      if (isHover() || isManual() || isFocus()) return

      const popoverEl = popoverRef.value
      if (popoverEl && popoverEl.contains(e.target)) return
      if (targetEl && targetEl.contains(e.target)) return

      hidePopover()
    }

    function bindTargetListeners () {
      if (!targetEl || isManual()) return

      if (isHover()) {
        attachListener(targetEl, 'mouseenter', showPopover)
        attachListener(targetEl, 'mouseleave', scheduleHide)
        return
      }

      if (isFocus()) {
        attachListener(targetEl, 'focus', showPopover)
        attachListener(targetEl, 'blur', hidePopover)
        return
      }

      targetClickHandler = () => {
        if (visible.value) hidePopover()
        else showPopover()
      }
      attachListener(targetEl, 'click', targetClickHandler)
    }

    function unbindTargetListeners () {
      if (!targetEl) return

      detachListener(targetEl, 'mouseenter', showPopover)
      detachListener(targetEl, 'mouseleave', scheduleHide)
      detachListener(targetEl, 'focus', showPopover)
      detachListener(targetEl, 'blur', hidePopover)

      if (targetClickHandler) {
        detachListener(targetEl, 'click', targetClickHandler)
        targetClickHandler = null
      }
    }

    // Keep manual trigger state in sync with the Vue 3 v-model contract.
    watch(() => getModelValue(), (val) => {
      if (isManual() && val !== null) {
        if (val) showPopover()
        else hidePopover()
      }
    })

    onMounted(() => {
      targetEl = resolveTarget(props.target)
      bindTargetListeners()

      attachListener(document, 'click', onDocumentClick, true)
      attachListener(window, 'resize', repositionPopover)
      attachListener(window, 'scroll', repositionPopover, true)

      // Check initial model value for manual trigger
      const initialVal = getModelValue()
      if (isManual() && initialVal) {
        showPopover()
      }
    })

    onBeforeUnmount(() => {
      unbindTargetListeners()
      detachListener(document, 'click', onDocumentClick, true)
      detachListener(window, 'resize', repositionPopover)
      detachListener(window, 'scroll', repositionPopover, true)
      stopTrackingPosition()
      if (hideTimeout) clearTimeout(hideTimeout)
    })

    return {
      visible,
      popoverRef,
      setPopoverRef,
      showPopover,
      hidePopover,
      onPopoverMouseenter,
      onPopoverMouseleave,
      getTargetEl () { return targetEl }
    }
  },
  watch: {
    visible (val) {
      if (val) {
        nextTick(() => {
          const el = this.popoverRef
          if (el) {
            const targetEl = this.getTargetEl()
            if (targetEl) {
              positionPopover(targetEl, el, this.placement)
            }
          }
        })
      }
    }
  },
  render () {
    const rawDefault = this.$slots.default
    const defaultSlot = normalizarVNodes(typeof rawDefault === 'function' ? rawDefault() : (rawDefault || []))
    const rawPopover = this.$slots.popover
    const popoverSlot = normalizarVNodes(typeof rawPopover === 'function' ? rawPopover() : (rawPopover || []))

    const result = [...defaultSlot]

    if (this.visible) {
      const activePlacement = this.popoverRef?.dataset.pttPopoverPlacement || this.placement
      const popoverClasses = ['popover', activePlacement, 'in', 'ptt-popover-active']
      if (this.customClass) popoverClasses.push(this.customClass)

      const popoverContent = []
      popoverContent.push(h('div', { class: 'arrow' }))

      if (this.title) {
        popoverContent.push(h('h3', { class: 'popover-title' }, this.title))
      }

      popoverContent.push(
        h('div', { class: 'popover-content' }, popoverSlot)
      )

      const popoverEl = h('div', {
        ref: this.setPopoverRef,
        class: popoverClasses.join(' '),
        style: {
          display: 'block',
          position: 'fixed',
          zIndex: '2147483647',
          pointerEvents: 'auto',
          isolation: 'isolate',
          transform: 'translateZ(0)'
        },
        onMousedown: this.enterable ? this.onPopoverMouseenter : undefined,
        onMouseenter: this.enterable ? this.onPopoverMouseenter : undefined,
        onMouseleave: this.enterable ? this.onPopoverMouseleave : undefined
      }, popoverContent)

      result.push(h(Teleport, { to: 'body' }, [popoverEl]))
    }

    return h(Fragment, null, result)
  }
}
